import React from "react";
import { Image, Page, Text, View } from "@react-pdf/renderer";
import type { PrintTemplate } from "@repo/types";
import { imageService } from "@repo/ui/lib/services";
import {
  PDFHeader,
  PDFInfoField,
  PDFRiskIndicator,
  PDFSectionHeader,
  PdfEssenceIllustrationPage,
  convertImageUrlForPDF,
  pdfStyles,
} from "../../shared";
import type {
  ResolvedItem,
  ResolvedSection,
} from "../../arbre/print/template/types";
import type { EpaysageCtx } from "./registry";
import { resolveEpaysagePages } from "./variants";

export interface PdfEpaysageTemplateProps {
  template: PrintTemplate;
  data: EpaysageCtx["data"];
  inventaireId: number;
}

const colorStyle = (backgroundColor?: string, color?: string) => ({
  ...(backgroundColor ? { backgroundColor } : {}),
  ...(color ? { color } : {}),
});

const small = { width: "24%", height: 100, objectFit: "cover" } as const;
const cell = { padding: 8, borderRight: "1 solid #e5e7eb" } as const;

const photoBox = (src: string, key: string) => (
  <View key={key} style={pdfStyles.photoContainerSmall}>
    <Image
      src={convertImageUrlForPDF(src, { width: 700 })}
      style={pdfStyles.photoSmall}
    />
  </View>
);

const RiskLine = ({ label, risk }: { label: string; risk: string }) => (
  <View style={pdfStyles.infoFieldRow}>
    <Text style={pdfStyles.infoLabel}>{label} : </Text>
    <PDFRiskIndicator risk={risk} showLabel={true} size="small" />
  </View>
);

function renderBlock(
  item: ResolvedItem,
  data: EpaysageCtx["data"],
  essence: any | null,
) {
  switch (item.key) {
    case "bloc_risque_global":
      return (
        <RiskLine
          key={item.key}
          label={item.label}
          risk={data.risque.general}
        />
      );
    case "bloc_photos":
      return data.img1 || data.img2 || data.img3 ? (
        <View key={item.key} style={pdfStyles.grid3}>
          {[data.img1, data.img2, data.img3].map((src, i) =>
            src ? photoBox(src, String(i)) : null,
          )}
        </View>
      ) : null;
    case "bloc_commentaire":
      return (
        <View key={item.key} style={pdfStyles.commentsWithBorder}>
          <Text style={pdfStyles.commentsLabel}>{item.label} : </Text>
          <Text style={pdfStyles.commentsValueNoBorder}>{data.comments}</Text>
        </View>
      );
    case "bloc_essence_photos":
      return essence?.img1 || essence?.img2 ? (
        <View key={item.key} style={pdfStyles.grid2}>
          {essence.img1 && photoBox(essence.img1, "1")}
          {essence.img2 && photoBox(essence.img2, "2")}
        </View>
      ) : null;
    case "bloc_essence_risque":
      return (
        <RiskLine key={item.key} label={item.label} risk={essence?.risque} />
      );
  }
  return null;
}

/** Sections sans filet vertical entre les colonnes dans la fiche actuelle. */
const NO_LEFT_BORDER = new Set(["identification", "risque_age", "photos"]);

function gridStyle(count: number) {
  return count >= 4
    ? pdfStyles.grid4
    : count === 3
      ? pdfStyles.grid3
      : count === 2
        ? pdfStyles.grid2
        : undefined;
}

function Sections({
  sections,
  data,
  essence,
}: {
  sections: ResolvedSection[];
  data: EpaysageCtx["data"];
  essence: any | null;
}) {
  return (
    <>
      {sections.map((section) => (
        <View key={section.id} wrap={false}>
          {section.title ? (
            section.plainTitle ? (
              <Text style={pdfStyles.travauxTitle}>{section.title}</Text>
            ) : (
              <PDFSectionHeader
                title={section.title}
                variant="secondary"
                style={colorStyle(
                  section.style?.titleBg,
                  section.style?.titleColor,
                )}
              />
            )
          ) : null}
          <View
            style={[
              ...(gridStyle(section.columns.length)
                ? [gridStyle(section.columns.length)!]
                : []),
              section.style?.background
                ? { backgroundColor: section.style.background }
                : {},
            ]}
          >
            {section.columns.map((column, index) => (
              <View
                key={index}
                style={
                  // Une section à une colonne n'est pas une grille : ses éléments se suivent comme dans la fiche actuelle
                  // (une colonne « flex » sans parent en ligne n'aurait pas de hauteur et les blocs se chevaucheraient).
                  section.columns.length === 1
                    ? undefined
                    : NO_LEFT_BORDER.has(section.id) ||
                        column.every((item) => item.kind === "block")
                      ? pdfStyles.columnNoLeft
                      : pdfStyles.column
                }
              >
                {column.map((item) =>
                  item.kind === "block" ? (
                    renderBlock(item, data, essence)
                  ) : (
                    <PDFInfoField
                      key={item.key}
                      label={item.label}
                      value={item.value}
                      layout="row"
                    />
                  ),
                )}
              </View>
            ))}
          </View>
        </View>
      ))}
    </>
  );
}

/**
 * Peintre PDF de la fiche de l'espace boisé : met en page le résultat de `resolveEpaysagePages` (aucune décision de
 * contenu ici). Retourne des `<Page>` à placer dans un `<Document>`.
 */
export function PdfEpaysageTemplate({
  template,
  data,
  inventaireId,
}: PdfEpaysageTemplateProps) {
  const { header } = template;
  const pages = resolveEpaysagePages(template, data, "pdf");

  return (
    <>
      <Page size="A4" style={pdfStyles.page}>
        <PDFHeader
          organizationName={data.organizationName}
          organizationAddress={data.organizationAddress}
          organizationCity={data.organizationCity}
          organizationZipCode={data.organizationZipCode}
          organizationSiret={
            header.showSiret ? data.organizationSiret : undefined
          }
          organizationNumCertification={data.organizationNumCertification}
          organizationLogo={data.organizationLogo}
          user={header.showRecenseur ? data.user : undefined}
          showLogo={header.showLogo}
          showOrganisation={header.showOrganisation}
          logoSize="small"
          marginBottom={10}
        />
        <PDFSectionHeader
          title={`FICHE DE SYNTHESE ESPACE BOISE - ${inventaireId}`}
          variant="epaysage"
          style={colorStyle(header.titleBg, header.titleColor)}
        />
        <Sections sections={pages.fiche} data={data} essence={null} />
      </Page>
      {pages.essences.map((sections, index) => {
        const essence = data.essence?.[index];
        const espece = essence?.espece as any;
        const name = data.essences[index] || `Essence ${index + 1}`;
        const illustration = espece?.img_url
          ? convertImageUrlForPDF(
              imageService.getOptimizedImageUrl(
                espece.img_url,
                "espece",
                800,
                600,
              ),
            )
          : null;
        return (
          <React.Fragment key={`essence-${index}`}>
            <Page size="A4" style={pdfStyles.page}>
              <Sections sections={sections} data={data} essence={essence} />
            </Page>
            <PdfEssenceIllustrationPage
              title={`ILLUSTRATION ESSENCE ${index + 1}`}
              latinName={espece?.name ?? name}
              frenchName={espece?.nom_fr ?? espece?.nomFr ?? null}
              imageUrl={illustration || null}
            />
          </React.Fragment>
        );
      })}
    </>
  );
}
